import React, { useState, useEffect } from 'react';

function Ring({ pct, size = 72, stroke = 3, color = 'var(--accent)' }) {
    const r = (size - stroke * 2) / 2;
    const circ = 2 * Math.PI * r;
    const dash = circ * (1 - pct / 100);
    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-track)" strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
                strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={dash} strokeLinecap="round" />
        </svg>
    );
}

const LEETCODE_USERNAME = 'byRVG6kZAS';

const LeetCodeStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`https://leetcode-api-faisalshohag.vercel.app/${LEETCODE_USERNAME}`);
                const data = await response.json();

                if (data && data.totalSolved !== undefined) {
                    let acceptanceRate = "0.00";
                    if (data.matchedUserStats && data.matchedUserStats.acSubmissionNum && data.matchedUserStats.totalSubmissionNum) {
                        const ac = data.matchedUserStats.acSubmissionNum[0].submissions;
                        const total = data.matchedUserStats.totalSubmissionNum[0].submissions;
                        if (total > 0) {
                            acceptanceRate = ((ac / total) * 100).toFixed(2);
                        }
                    }

                    setStats({
                        ...data,
                        contributionPoints: data.contributionPoint || 0,
                        acceptanceRate: acceptanceRate
                    });
                } else {
                    setError('Failed to load stats');
                }
            } catch (err) {
                console.error(err);
                setError('Network error');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                Loading LeetCode Stats...
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div style={{ padding: 16, textAlign: 'center', color: '#EF4444' }}>
                <p>LeetCode stats are temporarily unavailable.</p>
                <a href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>
                    View Profile ↗
                </a>
            </div>
        );
    }

    const easyPct = stats.easySolved / stats.totalEasy;
    const medPct = stats.mediumSolved / stats.totalMedium;
    const hardPct = stats.hardSolved / stats.totalHard;

    return (
        <div className="lc-card">
            <div className="lc-left">
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ring pct={Math.round((stats.totalSolved / stats.totalQuestions) * 100)} size={88} stroke={4} color="#FFA116" />
                    <div style={{ position: 'absolute', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>{stats.totalSolved}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Solved</div>
                    </div>
                </div>
            </div>
            <div className="lc-bars">
                {[
                    { label: 'Easy', solved: stats.easySolved, total: stats.totalEasy, color: '#22C55E', pct: easyPct },
                    { label: 'Medium', solved: stats.mediumSolved, total: stats.totalMedium, color: '#F59E0B', pct: medPct },
                    { label: 'Hard', solved: stats.hardSolved, total: stats.totalHard, color: '#EF4444', pct: hardPct }
                ].map(item => (
                    <div key={item.label} className="lc-bar-row">
                        <span className="lc-label" style={{ color: item.color }}>{item.label}</span>
                        <div className="lc-track">
                            <div className="lc-fill" style={{ width: `${item.pct * 100}%`, background: item.color }} />
                        </div>
                        <span className="lc-count">{item.solved} / {item.total}</span>
                    </div>
                ))}
            </div>
            <div className="lc-stats">
                {[
                    { label: 'Ranking', value: stats.ranking ? stats.ranking.toLocaleString() : 'N/A' },
                    { label: 'Acceptance', value: stats.acceptanceRate ? `${stats.acceptanceRate}%` : 'N/A' },
                    { label: 'Contributions', value: stats.contributionPoints ? stats.contributionPoints.toLocaleString() : '0' }
                ].map(s => (
                    <div key={s.label} className="lc-stat">
                        <span className="lc-stat-label">{s.label}</span>
                        <span className="lc-stat-value">{s.value}</span>
                    </div>
                ))}
                <a href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} target="_blank" rel="noreferrer" className="lc-link">View Profile ↗</a>
            </div>
        </div>
    );
};

export default LeetCodeStats;
